import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CompactSearch from './search/CompactSearch';
import ListingControls from './controls/ListingControls';
import ListingListView from './display/list/ListingListView';
import ListingCard from './display/grid/ListingCard';

function generateSampleListings() {
  const cityRegionPairs = [
    { city: 'Warszawa', region: 'Mazowieckie' },
    { city: 'Kraków', region: 'Małopolskie' },
    { city: 'Poznań', region: 'Wielkopolskie' },
    { city: 'Gdańsk', region: 'Pomorskie' },
    { city: 'Wrocław', region: 'Dolnośląskie' },
  ];

  const listings = [];
  for (let i = 1; i <= 60; i++) {
    const { city, region } = cityRegionPairs[i % cityRegionPairs.length];

    listings.push({
      id: i,
      title: `VW Golf 7 1.${i} TSI`,
      subtitle: '(dane z tabelek), (+ krótki opis sprzedającego + nagłówka, max 60 znaków)',
      price: (Math.floor(Math.random() * 50) + 10) * 1000,
      fuel: ['Benzyna', 'Diesel', 'Elektryczny', 'Hybrydowy'][i % 4],
      engineCapacity: `${1000 + Math.floor(Math.random() * 1500)} cm³`,
      power: `${50 + Math.floor(Math.random() * 150)} KM`,
      mileage: `${5000 + Math.floor(Math.random() * 200000)} km`,
      year: 1990 + Math.floor(Math.random() * 35),
      drive: ['Napęd przedni', 'Napęd tylny', '4x4'][i % 3],
      location: region,
      city: city,
      gearbox: ['Automatyczna', 'Manualna'][i % 2],
      sellerType: ['Prywatny', 'Firma'][i % 2],
      image: i % 2 === 0 ? '/images/auto-788747_1280.jpg' : '/images/car-1880381_640.jpg',
      featured: i <= 5,
      condition: i % 2 === 0 ? 'used' : 'new',
    });
  }
  return listings;
}

function ListingsPage() {
  const [listings] = useState(() => generateSampleListings());
  const [searchOpen, setSearchOpen] = useState(false);
  const [sortType, setSortType] = useState('none');
  const [offerType, setOfferType] = useState('all');
  const [onlyFeatured, setOnlyFeatured] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [itemsToShow, setItemsToShow] = useState(30);
  const [favorites, setFavorites] = useState([]);
  const [favMessages, setFavMessages] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  const navigate = useNavigate();

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 640);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const parseNum = (val) => parseInt(String(val).replace(/\D/g, ''), 10) || 0;

  // Sortowanie i filtrowanie
  let sortedListings = [...listings];
  switch (sortType) {
    case 'price-asc':
      sortedListings.sort((a, b) => parseNum(a.price) - parseNum(b.price));
      break;
    case 'price-desc':
      sortedListings.sort((a, b) => parseNum(b.price) - parseNum(a.price));
      break;
    case 'year-asc':
      sortedListings.sort((a, b) => a.year - b.year);
      break;
    case 'year-desc':
      sortedListings.sort((a, b) => b.year - a.year);
      break;
    case 'mileage-asc':
      sortedListings.sort((a, b) => parseNum(a.mileage) - parseNum(b.mileage));
      break;
    case 'mileage-desc':
      sortedListings.sort((a, b) => parseNum(b.mileage) - parseNum(a.mileage));
      break;
    default:
      break;
  }

  if (offerType !== 'all') {
    sortedListings = sortedListings.filter((item) => item.condition === offerType);
  }

  if (onlyFeatured) {
    sortedListings = sortedListings.filter((item) => item.featured);
  }

  const visibleListings = sortedListings.slice(0, itemsToShow);

  const toggleFavorite = (id) => {
    const isFav = favorites.includes(id);
    const msg = isFav ? 'Usunięto z ulubionych' : 'Dodano do ulubionych';
    
    setFavorites(prev => isFav ? prev.filter(x => x !== id) : [...prev, id]);
    setFavMessages(prev => ({ ...prev, [id]: msg }));
    
    setTimeout(() => {
      setFavMessages(prev => ({ ...prev, [id]: null }));
    }, 2000);
  };

  const handleShowMore = () => setItemsToShow(prev => prev + 30);
  const navigateToListing = (id) => navigate(`/listing/${id}`);

  const finalViewMode = isMobile ? 'list' : viewMode;

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-center mb-6">
          <button
            onClick={() => setSearchOpen(!searchOpen)}
            className="text-white px-6 py-2.5 rounded shadow-md hover:shadow-lg transition-all duration-300 bg-[#35530A] hover:bg-[#44671A]"
          >
            {searchOpen ? 'Ukryj wyszukiwarkę' : 'Pokaż wyszukiwarkę'}
          </button>
        </div>

        {searchOpen && <CompactSearch />}

        <ListingControls
          sortType={sortType}
          setSortType={setSortType}
          offerType={offerType}
          setOfferType={setOfferType}
          onlyFeatured={onlyFeatured}
          setOnlyFeatured={setOnlyFeatured}
          viewMode={viewMode}
          setViewMode={setViewMode}
          isMobile={isMobile}
        />

        <div className={
          finalViewMode === 'grid'
            ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6'
            : 'space-y-4 mt-6'
        }>
          {visibleListings.map(listing => (
            finalViewMode === 'grid' ? (
              <ListingCard
                key={listing.id}
                listing={listing}
                onNavigate={navigateToListing}
                onFavorite={toggleFavorite}
                isFavorite={favorites.includes(listing.id)}
                message={favMessages[listing.id]}
              />
            ) : (
              <ListingListView
                key={listing.id}
                listings={[listing]}
                onNavigate={navigateToListing}
                onFavorite={toggleFavorite}
                favorites={favorites}
                favMessages={favMessages}
              />
            )
          ))}
        </div>

        {itemsToShow < sortedListings.length && (
          <div className="text-center mt-8">
            <button
              onClick={handleShowMore}
              className="px-8 py-3 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 bg-[#35530A] hover:bg-[#44671A]"
            >
              Pokaż więcej
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ListingsPage;